import React, { useEffect, useRef, useState } from 'react';
import { nombre } from './format';

/**
 * Filtres légers en pilules, au-dessus de la grille (maquette du 2026-09-14).
 *
 * Chaque pilule ouvre un panneau d'options. Les comptes viennent du serveur
 * (facettes) : ils disent ce qu'on obtiendrait en choisissant l'option, les
 * autres filtres restant posés. Une option statique (année, kilométrage, tri)
 * n'a pas de compte.
 *
 * `dims` : [{ cle, titre, options?, defaut?, siMarque? }]
 */
export default function FiltresAffaires({ dims, facettes, criteres, onChange, total, libelleTotal }) {
  const [ouvert, setOuvert] = useState(null);
  const racine = useRef(null);

  useEffect(() => {
    if (!ouvert) return undefined;
    function fermer(e) {
      if (e.key === 'Escape') setOuvert(null);
    }
    function dehors(e) {
      if (racine.current && !racine.current.contains(e.target)) setOuvert(null);
    }
    document.addEventListener('keydown', fermer);
    document.addEventListener('mousedown', dehors);
    return () => {
      document.removeEventListener('keydown', fermer);
      document.removeEventListener('mousedown', dehors);
    };
  }, [ouvert]);

  const visibles = dims.filter((d) => !d.siMarque || criteres.marque);
  const dimOuverte = visibles.find((d) => d.cle === ouvert);

  function options(d) {
    return d.options || (facettes && facettes[d.cle]) || [];
  }

  function libelleActif(d) {
    const v = criteres[d.cle];
    if (!v || v === d.defaut) return d.titre;
    const o = options(d).find((x) => String(x.v) === String(v));
    return o ? o.court || o.l : d.titre;
  }

  function choisir(d, v) {
    const suivant = { ...criteres };
    delete suivant.page;
    if (String(criteres[d.cle]) === String(v) || v === d.defaut) delete suivant[d.cle];
    else suivant[d.cle] = v;
    if (d.cle === 'marque') delete suivant.modele;
    setOuvert(null);
    onChange(suivant);
  }

  const actifs = visibles.filter((d) => criteres[d.cle] && criteres[d.cle] !== d.defaut && d.cle !== 'tri');

  function effacer() {
    const suivant = { ...criteres };
    actifs.forEach((d) => delete suivant[d.cle]);
    delete suivant.page;
    onChange(suivant);
  }

  return (
    <div className="zk-aff-filtres" ref={racine}>
      <div className="zk-aff-pilules" role="toolbar" aria-label="Filtres">
        {visibles.map((d) => {
          const actif = criteres[d.cle] && criteres[d.cle] !== d.defaut;
          return (
            <button
              key={d.cle}
              type="button"
              className={`zk-aff-pilule${actif ? ' on' : ''}`}
              aria-expanded={ouvert === d.cle}
              aria-controls="zk-aff-panneau"
              onClick={() => setOuvert(ouvert === d.cle ? null : d.cle)}
            >
              {libelleActif(d)}
              <svg width="10" height="6" viewBox="0 0 12 8" aria-hidden="true">
                <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          );
        })}
        {actifs.length > 0 && (
          <button type="button" className="zk-aff-effacer" onClick={effacer}>Effacer</button>
        )}
        {total != null && (
          <span className="zk-aff-compte" aria-live="polite">
            <b>{nombre(total)}</b> {libelleTotal || (total > 1 ? 'affaires' : 'affaire')}
          </span>
        )}
      </div>

      {dimOuverte && (
        <div className="zk-aff-panneau" id="zk-aff-panneau">
          <h2>{dimOuverte.titre}</h2>
          {options(dimOuverte).length ? (
            <div className="zk-aff-options">
              {options(dimOuverte).map((o) => (
                <button
                  key={o.v}
                  type="button"
                  className="zk-aff-option"
                  aria-pressed={String(criteres[dimOuverte.cle] || dimOuverte.defaut || '') === String(o.v)}
                  onClick={() => choisir(dimOuverte, o.v)}
                >
                  {o.l}
                  {o.n != null && <small>{nombre(o.n)}</small>}
                </button>
              ))}
            </div>
          ) : (
            <p className="zk-aff-vide">Aucune option avec les filtres actuels.</p>
          )}
        </div>
      )}
    </div>
  );
}
